import Phaser from 'phaser';
import type { TokenCarStyle } from '../TokenRenderer.js';
import type { GridStyle } from './TrackRenderer';
import type { GameScene } from '../GameScene.js';

/**
 * Panneau session de la vue Jouer : dé / pilotes, live course.
 * La création de circuits se fait sur la page Circuits (#circuits).
 * Caméra 3D : sélecteur dans la barre de dé (`DiceUI`), plus de modale Paramètres.
 */
export class TrackGeneratorUI {
  private onGridStyleCallback: ((style: GridStyle) => void) | null = null;
  private onTokenStyleCallback: ((style: TokenCarStyle) => void) | null = null;

  private playSessionEmpty: HTMLDivElement | null = null;
  private diceMountEl: HTMLDivElement | null = null;
  private racePanel: HTMLDivElement | null = null;
  private sessionOnRight = false;
  private flipBtn: HTMLButtonElement | null = null;

  private static readonly SESSION_SIDE_KEY = 'multirx-play-session-side';

  constructor(_scene: Phaser.Scene) {
    this.createUI(_scene);
  }

  private createUI(_scene: Phaser.Scene): void {
    const gameView = document.getElementById('view-course') ?? document.body;

    this.sessionOnRight = sessionStorage.getItem(TrackGeneratorUI.SESSION_SIDE_KEY) === 'right';
    this.applySessionSide(gameView);

    const racePanel = document.createElement('aside');
    racePanel.className = 'course-race-panel play-panel play-panel--session';
    racePanel.setAttribute('aria-label', 'Session de course');

    const head = document.createElement('div');
    head.className = 'play-panel-head';
    const headIntro = document.createElement('div');
    headIntro.className = 'play-panel-head-intro';
    const title = document.createElement('h2');
    title.className = 'play-panel-title';
    title.textContent = 'Session';
    const lead = document.createElement('p');
    lead.className = 'play-panel-lead';
    lead.textContent = 'Circuit, pilotes et lancers — la caméra se règle dans la barre en bas.';
    headIntro.append(title, lead);
    this.flipBtn = document.createElement('button');
    this.flipBtn.type = 'button';
    this.flipBtn.className = 'flip-btn';
    this.flipBtn.textContent = '⟷';
    this.flipBtn.addEventListener('click', () => this.toggleSessionSide(gameView));
    head.append(headIntro, this.flipBtn);
    this.updateFlipButtonLabel();
    racePanel.appendChild(head);

    const sessionBody = document.createElement('div');
    sessionBody.className = 'play-panel-body';

    this.playSessionEmpty = document.createElement('div');
    this.playSessionEmpty.className = 'play-session-empty';
    this.playSessionEmpty.innerHTML =
      '<strong>Préparer la course</strong><p>Choisissez d’abord comment vous voulez jouer, puis suivez les étapes du panneau.</p>';

    this.diceMountEl = document.createElement('div');
    this.diceMountEl.id = 'course-dice-mount';
    this.diceMountEl.className = 'course-dice-mount';

    sessionBody.appendChild(this.playSessionEmpty);
    sessionBody.appendChild(this.diceMountEl);
    racePanel.appendChild(sessionBody);
    this.racePanel = racePanel as HTMLDivElement;

    const gameMain = gameView.querySelector('.game-main');
    if (gameMain) {
      gameView.insertBefore(racePanel, gameMain);
    } else {
      gameView.appendChild(racePanel);
    }
    this.ensureCourseFabBar(gameView);
  }

  private toggleSessionSide(gameView: HTMLElement): void {
    this.sessionOnRight = !this.sessionOnRight;
    sessionStorage.setItem(
      TrackGeneratorUI.SESSION_SIDE_KEY,
      this.sessionOnRight ? 'right' : 'left'
    );
    this.applySessionSide(gameView);
    this.updateFlipButtonLabel();
    window.dispatchEvent(new Event('multirx-play-layout-change'));
  }

  private applySessionSide(gameView: HTMLElement): void {
    gameView.classList.toggle('play-session-right', this.sessionOnRight);
  }

  private updateFlipButtonLabel(): void {
    if (!this.flipBtn) return;
    this.flipBtn.title = this.sessionOnRight
      ? 'Placer le panneau à gauche'
      : 'Placer le panneau à droite';
    this.flipBtn.setAttribute(
      'aria-label',
      this.sessionOnRight ? 'Panneau session à droite' : 'Panneau session à gauche'
    );
  }

  private ensureCourseFabBar(gameView: HTMLElement): void {
    if (gameView.querySelector('.course-fab-bar')) return;
    const bar = document.createElement('div');
    bar.className = 'course-fab-bar';
    const btnSession = document.createElement('button');
    btnSession.type = 'button';
    btnSession.className = 'btn btn-primary';
    btnSession.textContent = 'Session';
    btnSession.addEventListener('click', () => {
      gameView.classList.toggle('course-drawer-race-open');
    });
    bar.appendChild(btnSession);
    gameView.appendChild(bar);
  }

  public getDiceMount(): HTMLElement {
    if (!this.diceMountEl) throw new Error('Zone dé non initialisée');
    return this.diceMountEl;
  }

  public setRaceStarted(raceStarted: boolean): void {
    if (this.playSessionEmpty) this.playSessionEmpty.hidden = raceStarted;
  }

  /** Conservé pour compatibilité GameScene — le classement live est sur le HUD. */
  public refreshRaceStats(_scene: GameScene): void {
    /* no-op */
  }

  public onGridStyleChange(callback: (style: GridStyle) => void): void {
    this.onGridStyleCallback = callback;
  }

  public onTokenStyleChange(callback: (style: TokenCarStyle) => void): void {
    this.onTokenStyleCallback = callback;
  }

  public showError(message: string): void {
    let errorDiv = document.getElementById('track-generator-error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.id = 'track-generator-error';
      errorDiv.className = 'toast toast--error';
      errorDiv.setAttribute('role', 'alert');
      document.body.appendChild(errorDiv);
    }
    errorDiv.textContent = message;
    errorDiv.hidden = false;
    setTimeout(() => {
      if (errorDiv) errorDiv.hidden = true;
    }, 4000);
  }

  public destroy(): void {
    this.racePanel?.remove();
    document.getElementById('view-course')?.classList.remove('play-session-right');
    const errorDiv = document.getElementById('track-generator-error');
    if (errorDiv) errorDiv.remove();
    document.querySelector('.course-fab-bar')?.remove();
    this.diceMountEl = null;
    this.racePanel = null;
    this.playSessionEmpty = null;
    this.onGridStyleCallback = null;
    this.onTokenStyleCallback = null;
  }
}
